import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { axiosInstance } from '../App';
import { Loader2, Shield, Wrench, User, Download, Mail, ArrowLeft, Share, Plus, MoreVertical, X, Smartphone } from 'lucide-react';

export default function UnifiedLogin({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [activeTab, setActiveTab] = useState('admin');
  const [isStandalone, setIsStandalone] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [isMobile] = useState(() => {
    const ua = window.navigator.userAgent.toLowerCase();
    return /iphone|ipad|ipod|android|mobile/.test(ua) || window.innerWidth < 768;
  });
  const [isIOS] = useState(() => /iphone|ipad|ipod/.test(window.navigator.userAgent.toLowerCase()));
  const [isAndroid] = useState(() => /android/.test(window.navigator.userAgent.toLowerCase()));
  const [installPromptDismissed, setInstallPromptDismissed] = useState(false);
  const navigate = useNavigate();

  // Check for existing session on mount
  useEffect(() => {
    const checkExistingSession = async () => {
      const token = localStorage.getItem('pwa_token');
      const user = localStorage.getItem('pwa_user');
      
      if (token && user) {
        try {
          const userData = JSON.parse(user);
          onLogin(userData, token);
          const adminRoles = ['admin', 'super_admin', 'manager'];
          if (adminRoles.includes(userData.role)) {
            navigate('/admin');
          } else if (userData.role === 'handyman') {
            navigate('/handyman');
          } else if (userData.role === 'customer') {
            navigate('/customer');
          }
          return;
        } catch (error) {
          localStorage.removeItem('pwa_token');
          localStorage.removeItem('pwa_user');
        }
      }
      setCheckingSession(false);
    };

    checkExistingSession();
    
    setIsStandalone(window.matchMedia('(display-mode: standalone)').matches || 
                    window.navigator.standalone === true);
  }, [onLogin, navigate]);

  // OTP Timer countdown
  useEffect(() => {
    let interval;
    if (otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpTimer]);

  // PWA Install Prompt
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      toast.success('App installed successfully!');
    }
    setDeferredPrompt(null);
    setShowInstallPrompt(false);
  };

  // Request OTP for customer login
  const handleRequestOtp = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axiosInstance.post('/auth/customer/request-otp', { email });
      setOtpSent(true);
      setOtpTimer(60); // 60 seconds before resend
      
      // Check if we're in testing mode
      if (response.data?.note?.includes('testing mode')) {
        toast.info('OTP generated! Check server logs for the code (email service in testing mode)', {
          duration: 8000
        });
      } else {
        toast.success('OTP sent to your email!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  // Verify OTP and login customer
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otp || otp.length !== 6) {
      toast.error('Please enter the 6-digit OTP');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axiosInstance.post('/auth/customer/verify-otp', {
        email,
        otp_code: otp
      });

      const { token, user, customer } = response.data;

      // Store session persistently
      localStorage.setItem('pwa_token', token);
      localStorage.setItem('pwa_user', JSON.stringify(user));
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('customer', JSON.stringify(customer));

      toast.success(`Welcome, ${user.name}!`);
      onLogin(user, token);
      navigate('/customer');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    if (otpTimer > 0) return;
    
    setLoading(true);
    try {
      await axiosInstance.post('/auth/customer/resend-otp', { email });
      setOtpTimer(60);
      toast.success('OTP resent to your email!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  // Admin/Handyman password login
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axiosInstance.post('/auth/login', {
        email,
        password
      });

      const { token, user } = response.data;

      const adminRoles = ['admin', 'super_admin', 'manager'];
      if (activeTab === 'admin' && !adminRoles.includes(user.role)) {
        toast.error('Invalid credentials for Admin login');
        setLoading(false);
        return;
      }
      if (activeTab === 'handyman' && user.role !== 'handyman') {
        toast.error('Invalid credentials for Handyman login');
        setLoading(false);
        return;
      }

      localStorage.setItem('pwa_token', token);
      localStorage.setItem('pwa_user', JSON.stringify(user));
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      if (user.role === 'handyman') {
        localStorage.setItem('handyman_token', token);
        localStorage.setItem('handyman_user', JSON.stringify(user));
      }

      toast.success(`Welcome, ${user.name}!`);
      onLogin(user, token);
      
      if (adminRoles.includes(user.role)) {
        navigate('/admin');
      } else if (user.role === 'handyman') {
        navigate('/handyman');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking session
  if (checkingSession) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#EFF7FF' }}>
        <div className="text-center text-gray-600">
          <Loader2 className="animate-spin h-12 w-12 mx-auto mb-4" />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return <Shield className="h-5 w-5" />;
      case 'handyman': return <Wrench className="h-5 w-5" />;
      case 'customer': return <User className="h-5 w-5" />;
      default: return null;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'from-gray-800 to-gray-900';
      case 'handyman': return 'from-teal-600 to-teal-700';
      case 'customer': return 'from-blue-600 to-blue-700';
      default: return 'from-gray-600 to-gray-700';
    }
  };

  // Reset OTP state when switching tabs
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setOtpSent(false);
    setOtp('');
    setEmail('');
    setPassword('');
  };

  const showMobileInstallBanner = isMobile && !isStandalone && !installPromptDismissed;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 safe-area-inset" style={{ backgroundColor: '#EFF7FF' }}>
      {/* Logo Section */}
      <div className="mb-6 text-center">
        <img 
          src="https://customer-assets.emergentagent.com/job_1dafeb02-9ec2-4436-8d6b-af3019fabee1/artifacts/je75u1q2_White%20logo%20thick%20copy.jpg" 
          alt="Redefyne Logo" 
          className="w-48 h-auto mx-auto mb-4 rounded-2xl shadow-2xl"
        />
      </div>

      {/* Mobile PWA Install Prompt */}
      {showMobileInstallBanner && (
        <div data-testid="pwa-install-banner" className="w-full max-w-sm mb-4 animate-in slide-in-from-top-4 duration-500">
          <Card className="bg-[#3a3a3a] border-0 shadow-2xl rounded-2xl overflow-hidden">
            <div className="relative">
              <button
                data-testid="pwa-install-dismiss"
                onClick={() => setInstallPromptDismissed(true)}
                className="absolute top-3 right-3 text-white/50 hover:text-white/90 transition-colors z-10"
                aria-label="Dismiss install prompt"
              >
                <X className="w-5 h-5" />
              </button>
              <CardContent className="pt-5 pb-4 px-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-teal-600 flex items-center justify-center flex-shrink-0">
                    <Smartphone className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">Install Redefyne App</p>
                    <p className="text-white/60 text-xs">Quick access from your home screen</p>
                  </div>
                </div>

                {/* Android: One-tap install or manual steps */}
                {(isAndroid || (!isIOS && !isAndroid)) && deferredPrompt && (
                  <Button
                    data-testid="pwa-install-button"
                    onClick={handleInstallClick}
                    className="w-full bg-teal-600 hover:bg-teal-500 text-white h-10 text-sm font-medium rounded-xl"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Install Now
                  </Button>
                )}

                {/* Android manual steps (no deferredPrompt) */}
                {(isAndroid || (!isIOS && !isAndroid)) && !deferredPrompt && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2.5 text-white/80">
                      <div className="w-6 h-6 rounded-full bg-teal-600/30 flex items-center justify-center flex-shrink-0">
                        <span className="text-teal-400 text-xs font-bold">1</span>
                      </div>
                      <p className="text-xs">Tap <MoreVertical className="w-3.5 h-3.5 inline -mt-0.5" /> menu in your browser</p>
                    </div>
                    <div className="flex items-center gap-2.5 text-white/80">
                      <div className="w-6 h-6 rounded-full bg-teal-600/30 flex items-center justify-center flex-shrink-0">
                        <span className="text-teal-400 text-xs font-bold">2</span>
                      </div>
                      <p className="text-xs">Select "Add to Home Screen"</p>
                    </div>
                  </div>
                )}

                {/* iOS instructions */}
                {isIOS && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2.5 text-white/80">
                      <div className="w-6 h-6 rounded-full bg-teal-600/30 flex items-center justify-center flex-shrink-0">
                        <span className="text-teal-400 text-xs font-bold">1</span>
                      </div>
                      <p className="text-xs">Tap <Share className="w-3.5 h-3.5 inline -mt-0.5" /> Share at the bottom</p>
                    </div>
                    <div className="flex items-center gap-2.5 text-white/80">
                      <div className="w-6 h-6 rounded-full bg-teal-600/30 flex items-center justify-center flex-shrink-0">
                        <span className="text-teal-400 text-xs font-bold">2</span>
                      </div>
                      <p className="text-xs">Scroll & tap <Plus className="w-3.5 h-3.5 inline -mt-0.5 border rounded-sm border-white/40" /> "Add to Home Screen"</p>
                    </div>
                    <p className="text-white/40 text-[10px] mt-1 pl-8">Use Safari browser for best experience</p>
                  </div>
                )}

                <button
                  data-testid="pwa-install-skip"
                  onClick={() => setInstallPromptDismissed(true)}
                  className="w-full text-center text-white/40 hover:text-white/60 text-xs mt-3 transition-colors"
                >
                  Skip, continue to login
                </button>
              </CardContent>
            </div>
          </Card>
        </div>
      )}

      {/* Login Card */}
      <Card className="w-full max-w-sm backdrop-blur-lg bg-white/95 shadow-2xl border-0 rounded-2xl">
        <CardHeader className="text-center pb-2">
          <CardTitle className="text-xl">Welcome</CardTitle>
          <CardDescription>Sign in to your account</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Role Tabs */}
          <Tabs value={activeTab} onValueChange={handleTabChange} className="mb-4">
            <TabsList className="grid grid-cols-3 w-full">
              <TabsTrigger value="admin" className="flex items-center gap-1 text-xs" data-testid="login-tab-admin">
                <Shield size={14} />
                Admin
              </TabsTrigger>
              <TabsTrigger value="handyman" className="flex items-center gap-1 text-xs" data-testid="login-tab-handyman">
                <Wrench size={14} />
                Handyman
              </TabsTrigger>
              <TabsTrigger value="customer" className="flex items-center gap-1 text-xs" data-testid="login-tab-customer">
                <User size={14} />
                Customer
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Customer OTP Login */}
          {activeTab === 'customer' ? (
            <div className="space-y-4">
              {!otpSent ? (
                // Step 1: Enter Email
                <form onSubmit={handleRequestOtp} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="customer-email">Email</Label>
                    <Input
                      id="customer-email"
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="h-12 text-base"
                      autoComplete="email"
                      data-testid="customer-email-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    className={`w-full h-12 text-base bg-gradient-to-r ${getRoleColor(activeTab)} hover:opacity-90`}
                    disabled={loading}
                    data-testid="send-otp-button"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Sending OTP...
                      </>
                    ) : (
                      <>
                        <Mail className="mr-2 h-5 w-5" />
                        Send OTP
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-gray-500 text-center">
                    We'll send a 6-digit code to your email
                  </p>
                </form>
              ) : (
                // Step 2: Enter OTP
                <form onSubmit={handleVerifyOtp} className="space-y-4">
                  <div className="text-center mb-4">
                    <p className="text-sm text-gray-600">
                      OTP sent to <strong>{email}</strong>
                    </p>
                    <button
                      type="button"
                      onClick={() => { setOtpSent(false); setOtp(''); }}
                      className="text-sm text-blue-600 hover:underline flex items-center justify-center gap-1 mt-1 mx-auto"
                    >
                      <ArrowLeft size={14} />
                      Change email
                    </button>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="otp">Enter OTP</Label>
                    <Input
                      id="otp"
                      type="text"
                      placeholder="Enter 6-digit code"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      required
                      className="h-14 text-2xl text-center tracking-widest font-mono"
                      maxLength={6}
                      autoComplete="one-time-code"
                      data-testid="otp-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    className={`w-full h-12 text-base bg-gradient-to-r ${getRoleColor(activeTab)} hover:opacity-90`}
                    disabled={loading || otp.length !== 6}
                    data-testid="verify-otp-button"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      <>
                        <User className="mr-2 h-5 w-5" />
                        Verify & Sign In
                      </>
                    )}
                  </Button>
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={otpTimer > 0 || loading}
                      className={`text-sm ${otpTimer > 0 ? 'text-gray-400' : 'text-blue-600 hover:underline'}`}
                    >
                      {otpTimer > 0 ? `Resend OTP in ${otpTimer}s` : 'Resend OTP'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          ) : (
            // Admin/Handyman Password Login
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-12 text-base"
                  autoComplete="email"
                  data-testid="email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-12 text-base"
                  autoComplete="current-password"
                  data-testid="password-input"
                />
              </div>
              <Button
                type="submit"
                className={`w-full h-12 text-base bg-gradient-to-r ${getRoleColor(activeTab)} hover:opacity-90`}
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  <>
                    {getRoleIcon(activeTab)}
                    <span className="ml-2">Sign In as {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}</span>
                  </>
                )}
              </Button>
            </form>
          )}

          {/* Desktop Install App Button (non-mobile fallback) */}
          {showInstallPrompt && !isStandalone && !isMobile && (
            <div className="mt-4 pt-4 border-t">
              <Button
                variant="outline"
                className="w-full h-12 text-base"
                onClick={handleInstallClick}
                data-testid="desktop-install-button"
              >
                <Download className="mr-2 h-5 w-5" />
                Install App
              </Button>
              <p className="text-xs text-gray-500 text-center mt-2">
                Add to home screen for quick access
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="mt-6 text-center text-gray-500 text-xs space-y-1">
        <p className="font-medium">2301 S Stearman Dr, Chandler, AZ 85286</p>
        <p>+1 602-715-1313 | support@redefyneaz.com</p>
        <p className="text-gray-400 mt-2">&copy; 2025 Redefyne Interior & Exterior</p>
      </div>
    </div>
  );
}
